import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import type {
  WebsiteSignInsResp,
  ConnectWebsiteResp,
  ClearWebsiteSessionReq,
  SavedBrowserSite,
} from "@/types/browserSignIn.ts";

export type WebsiteSignInClient = {
  list: (signal: AbortSignal) => Promise<WebsiteSignInsResp>;
  connectBrowser: (websiteUrl: string) => Promise<ConnectWebsiteResp>;
  clearSession: (request: ClearWebsiteSessionReq) => Promise<void>;
  createOperationId: () => string;
};
export const websiteSignInsKey = ["website-sign-ins"] as const;
const key = websiteSignInsKey;

export function useWebsiteSignIns(client: WebsiteSignInClient) {
  const clearAttempts = useRef(new Map<string, ClearWebsiteSessionReq>());
  const queryClient = useQueryClient();
  const query = useQuery(
    queryOptions({
      queryKey: key,
      queryFn: ({ signal }) => client.list(signal),
      refetchOnMount: "always",
    }),
  );
  const inventoryCurrent = !query.isError && query.data?.browserData?.status === "current";
  const connect = useMutation({
    mutationFn: (websiteUrl: string) =>
      client.connectBrowser(websiteUrl.includes("://") ? websiteUrl : `https://${websiteUrl}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });
  const clear = useMutation({
    mutationFn: async (domain: string) => {
      const inventory = query.data?.browserData;
      if (!inventoryCurrent || !inventory?.revision)
        throw new Error("Refresh saved browser data before clearing a session.");
      let request = clearAttempts.current.get(domain);
      if (!request) {
        request = { domain, revision: inventory.revision, operationId: client.createOperationId() };
        clearAttempts.current.set(domain, request);
      }
      await client.clearSession(request);
    },
    onSuccess: (_result, domain) => {
      clearAttempts.current.delete(domain);
      return queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (error, domain) => {
      // A conflict did not mutate anything. Ambiguous provider failures keep the
      // original operation identity even if a refetch observes a newer snapshot.
      if (responseStatus(error) === 409) {
        clearAttempts.current.delete(domain);
        return queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });
  return {
    query,
    inventoryCurrent,
    connect,
    clear,
  };
}

export type WebsiteGroup = { domain: string; browserData: SavedBrowserSite };
export function websiteGroups(data?: WebsiteSignInsResp): WebsiteGroup[] {
  return (data?.browserData?.sites ?? [])
    .map((site) => ({ domain: site.domain, browserData: site }))
    .sort((a, b) => a.domain.localeCompare(b.domain));
}
export function filterWebsiteGroups(groups: WebsiteGroup[], search: string): WebsiteGroup[] {
  const term = search.trim().toLowerCase();
  if (!term) return groups;
  return groups.filter(({ domain, browserData }) =>
    [domain, ...browserData.cookieDomains, ...browserData.storageOrigins].some((value) =>
      value.toLowerCase().includes(term),
    ),
  );
}

export function inventoryNotice(
  data?: WebsiteSignInsResp,
  refreshFailed = false,
): string | undefined {
  if (!data) return undefined;
  if (refreshFailed)
    return "Couldn’t refresh saved browser data. Showing previously saved data. Refresh to try again.";
  if (!data.browserData || data.browserData.status === "unavailable")
    return "Saved browser data is unavailable. Refresh to try again. Your saved data has not been removed.";
  if (data.browserData.status === "stale")
    return "Showing previously saved browser data. Refresh to check for changes.";
  return undefined;
}

function responseStatus(error: unknown): unknown {
  if (typeof error !== "object" || !error || !("response" in error)) return undefined;
  const response = error.response;
  return typeof response === "object" && response && "status" in response
    ? response.status
    : undefined;
}

export function websiteOperationError(error: unknown): string {
  if (responseStatus(error) === 409)
    return "Browser is currently in use or saved data has changed. Refresh and try again.";
  if (responseStatus(error) === 400)
    return "Enter a domain or HTTPS website address, such as example.com.";
  return "Couldn’t finish this browser operation. Please try again.";
}

export const SESSION_EXPLANATION =
  "Sign in through the live browser to save a session for future tasks. Saved browser data does not guarantee the website still has you signed in.";
export function clearSessionConfirmation(domain: string) {
  return `Clear saved session for ${domain} and its subdomains? Other websites will be kept.`;
}
